import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, TrendingUpIcon, DollarSignIcon, ShieldCheckIcon, BarChart3Icon, StarIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const comparisonData = [{
  feature: 'Focus',
  oem: 'Product adoption',
  si: 'Large-scale development',
  staffAug: 'Bodies on contract',
  nrs: 'Operational governance'
}, {
  feature: 'Engagement Model',
  oem: 'Advisory (shallow)',
  si: 'Project-based (expensive)',
  staffAug: 'Time & materials',
  nrs: 'Fractional/Retainer'
}, {
  feature: 'Decision Authority',
  oem: 'Won\'t say "no"',
  si: 'Builds what you ask',
  staffAug: 'No strategic input',
  nrs: 'Acts as "brake" & "steering"'
}, {
  feature: 'Speed',
  oem: 'Slow (standardized)',
  si: 'Slow (contract-heavy)',
  staffAug: 'Variable',
  nrs: '30-60 day sprints'
}, {
  feature: 'Cost',
  oem: 'High (Impact = 20% ACV)',
  si: 'Very high (large teams)',
  staffAug: 'Medium',
  nrs: 'Moderate (expert-led)'
}, {
  feature: 'Incentive',
  oem: 'License renewals',
  si: 'Billable hours',
  staffAug: 'Staff utilization',
  nrs: 'Your independence'
}, {
  feature: 'Federal Expertise',
  oem: 'General',
  si: 'Deep but generic',
  staffAug: 'Variable',
  nrs: 'Defense specialized'
}, {
  feature: 'Outcome',
  oem: 'Feature adoption',
  si: 'Custom builds',
  staffAug: 'Capacity',
  nrs: 'Sustainable operations'
}];
export function CaseStudiesPage() {
  return <div className="w-full" data-id="element-918" data-mp="27u3qdoq64vwq">
      <ServiceNowSubNav data-id="element-919" data-mp="27tbro519yi28" />
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-920" data-mp="27s0puare3kdr">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-921" data-mp="2vimyo497x8ot">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-922" data-mp="30axzrafp30iy">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-923" data-mp="3649u9bylylmw">
              Case Studies
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-924" data-mp="3145b1e6bfcsz"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-925" data-mp="2w5kdxnmd198r">
              Real outcomes from federal agencies and enterprises that engaged
              New River Systems for governance and platform stewardship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED CASE STUDY */}
      <section className="section-white" data-id="element-926" data-mp="2id4h54k1y7ga">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-927" data-mp="2dgx6iygpo7s0">
          <div className="inline-block px-3 py-1 bg-nrs-orange/10 border border-nrs-orange/30 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-6" data-id="element-928" data-mp="28h1cns1q9wq6">
            Featured Case Study
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-929" data-mp="1tesxhjl9xr85">
            {/* Left: Challenge + Approach */}
            <div className="lg:col-span-2 space-y-6" data-id="element-930" data-mp="1yc2wnqygth96">
              <div data-id="element-931" data-mp="23c7crfvgo9yv">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-2" data-id="element-932" data-mp="23bnw02hjrrmf">
                  Federal Agency Platform Transformation
                </h2>
                <p className="text-gray-500 text-sm" data-id="element-933" data-mp="1anjmf5vuuueg">
                  Anonymized — Mid-size federal agency, 2,500 users
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-934" data-mp="1fn4kle37ayq4">
                <h3 className="font-heading font-bold text-base text-nrs-navy mb-3" data-id="element-935" data-mp="1kko8z6tm4rvs">
                  The Challenge
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-936" data-mp="1peit2bd4r8ve">
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-937" data-mp="rw8uqmib2dli">
                    <span className="text-red-500 font-bold" data-id="element-938" data-mp="wv3fhtzu2c89">•</span>
                    Platform: 2,500 users, ITSM + HRSD + Security Operations
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-939" data-mp="2tmhirqhm7s4b">
                    <span className="text-red-500 font-bold" data-id="element-940" data-mp="2tkd6ol9kbmsy">•</span>
                    Stuck on 2-year-old version, unable to upgrade
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-941" data-mp="33jlx5457yjak">
                    <span className="text-red-500 font-bold" data-id="element-942" data-mp="3hb0opzd6zst8">•</span>
                    40+ high-severity Instance Scan findings
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-943" data-mp="3dcjidi1limcv">
                    <span className="text-red-500 font-bold" data-id="element-944" data-mp="3r96rej6tp2z5">•</span>
                    No formal governance framework in place
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-945" data-mp="1wvfd36djtuxd">
                    <span className="text-red-500 font-bold" data-id="element-946" data-mp="1rx3zqlo1p2ae">•</span>
                    Mounting SI costs with no independent oversight
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-947" data-mp="7fldq8bspi13">
                    <span className="text-red-500 font-bold" data-id="element-948" data-mp="cclafsmr98fj">•</span>
                    Audit concerns and inability to demonstrate ROI
                  </div>
                </div>
              </div>

              <div className="bg-nrs-light rounded-xl p-6 border border-nrs-blue/20" data-id="element-949" data-mp="2ms7th4lc3nf">
                <h3 className="font-heading font-bold text-base text-nrs-navy mb-4" data-id="element-950" data-mp="3ksofrrqeal0p">
                  The Approach
                </h3>
                <div className="space-y-4" data-id="element-951" data-mp="3pqpy2afzygb4">
                  <div className="flex gap-4" data-id="element-952" data-mp="3aw20rjkr2o6l">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-953" data-mp="3axd3rh6dfrwn">
                      30d
                    </div>
                    <div data-id="element-954" data-mp="1922n4rk5nuqq">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-955" data-mp="1e39yf0libc25">
                        Phase 1: Discovery
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-956" data-mp="1cnnvd00dl4fg">
                        Technical debt audit, stakeholder interviews, governance
                        framework design
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-957" data-mp="yn3ewahxnkp4">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-958" data-mp="yoeioabdosg8">
                      60d
                    </div>
                    <div data-id="element-959" data-mp="1qfkmp1u7zaas">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-960" data-mp="1lmj52m2mowni">
                        Phase 2: Implementation
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-961" data-mp="1hkxmql7brq5x">
                        Governance board establishment, demand scoring
                        implementation, upgrade roadmap creation
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-962" data-mp="3s0eyfgkx5ptj">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-orange rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-963" data-mp="ssoj67lwr48">
                      ∞
                    </div>
                    <div data-id="element-964" data-mp="9th2k0x02rh4">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-965" data-mp="9tqvnxdcder1">
                        Phase 3: Ongoing
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-966" data-mp="eqr1q8a6yhi5">
                        Fractional Platform Owner retainer for continuous
                        oversight and governance
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="border-l-4 border-nrs-orange pl-5 py-2" data-id="element-967" data-mp="xjl668a6ur84">
                <p className="text-gray-700 text-base italic leading-relaxed mb-3" data-id="element-968" data-mp="12i5wnfzn1wjd">
                  "New River Systems gave us the strategic governance we
                  couldn't afford to hire full-time. For the first time, we can
                  confidently say 'no' to requests that don't serve our
                  mission."
                </p>
                <footer className="text-gray-500 text-sm font-semibold" data-id="element-969" data-mp="mf5fxwpff6r7">
                  — IT Director, Federal Agency
                </footer>
              </blockquote>
            </div>

            {/* Right: Results */}
            <div data-id="element-970" data-mp="3qrsw1kjbtl9p">
              <div className="bg-nrs-navy rounded-xl p-6 sticky top-24" data-id="element-971" data-mp="3luspl8ikegi2">
                <h3 className="font-heading font-bold text-white text-base mb-5" data-id="element-972" data-mp="3lspnhq450wwz">
                  The Results
                </h3>
                <div className="space-y-4" data-id="element-973" data-mp="3uvqrnl9g0dy0">
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-974" data-mp="3utxle72qrvqj">
                    <div className="flex items-center gap-2 mb-1" data-id="element-975" data-mp="375eadjsb54ur">
                      <TrendingUpIcon className="h-4 w-4 text-nrs-orange" data-id="element-976" data-mp="373js6d8f7qud" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-977" data-mp="3342kdd5iuhm1">
                        Upgrade Readiness
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-978" data-mp="2xrw2mn3b96vl">
                      58 → 91
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-979" data-mp="2j0dfm5c1ac2q">
                      Instance Scan score improvement
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-980" data-mp="3bs6rjkl28be4">
                    <div className="flex items-center gap-2 mb-1" data-id="element-981" data-mp="3gqi3c0ux0vyz">
                      <DollarSignIcon className="h-4 w-4 text-nrs-orange" data-id="element-982" data-mp="3gnm7qj6jijdp" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-983" data-mp="31sfzdpjz28h7">
                        Cost Optimization
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-984" data-mp="1qcrd70ejldhg">
                      $45K
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-985" data-mp="1va9b7vr0u8ao">
                      Unnecessary license costs identified
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-986" data-mp="taimlgvf89bv">
                    <div className="flex items-center gap-2 mb-1" data-id="element-987" data-mp="enh1t4s3z0ii">
                      <BarChart3Icon className="h-4 w-4 text-nrs-orange" data-id="element-988" data-mp="el35ytpltk9w" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-989" data-mp="jkfpb4h9o97q">
                        Demand Management
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-990" data-mp="am3roihzgb5s">
                      28%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-991" data-mp="3rvjzob9ho3q8">
                      Backlog requests rejected as low-value
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-992" data-mp="1q3dxrcyd6kp8">
                    <div className="flex items-center gap-2 mb-1" data-id="element-993" data-mp="1v1p9jt87z5a3">
                      <ShieldCheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-994" data-mp="1uytdybjugsot" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-995" data-mp="3fjhoo30t9ctb">
                        Governance Maturity
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-lg leading-tight" data-id="element-996" data-mp="3p9t1kf2h2wcc">
                      Functioning Board
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-997" data-mp="241xnmi1cz75">
                      Making objective decisions monthly
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-998" data-mp="3ua71f9tx6zza">
                    <div className="flex items-center gap-2 mb-1" data-id="element-999" data-mp="c16jfl7yysnf">
                      <StarIcon className="h-4 w-4 text-nrs-orange" data-id="element-1000" data-mp="72wn37aidm08" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-1001" data-mp="71ljb7h2ce94">
                        Platform ROI
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-1002" data-mp="28wm59w7ahg30">
                      2.1:1
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-1003" data-mp="hqq1gwo3jw1m">
                      Value-to-cost ratio demonstrated
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL CASE STUDIES */}
      <section className="section-light-blue" data-id="element-1004" data-mp="vqqzm4cz8pde">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1005" data-mp="vpzfpqsqsg2y">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1006" data-mp="10osgqjqueem8">
            Additional Engagements
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1007" data-mp="15hdchqtb71se"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1008" data-mp="19i5imb6vwqkg">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-1009" data-mp="1eh0cc86e3zjc">
              <div className="flex items-center gap-3 mb-4" data-id="element-1010" data-mp="1tk0dr0ujwqez">
                <div className="w-10 h-10 bg-nrs-navy rounded-lg flex items-center justify-center" data-id="element-1011" data-mp="1tll7io91k5bs">
                  <ShieldCheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1012" data-mp="1ojcfpv7wn9b9" />
                </div>
                <div data-id="element-1013" data-mp="1jmci88p4fevr">
                  <div className="font-heading font-bold text-nrs-navy text-base" data-id="element-1014" data-mp="1jnx3t8tgjnhh">
                    Defense Agency
                  </div>
                  <div className="text-gray-500 text-xs" data-id="element-1015" data-mp="2cb1dvs7eqs04">
                    Defense | Secure On-Premise Instances
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-1016" data-mp="22kgfo5vnbphq">
                Cross-instance platform governance across secure on-premise
                instances. Established data governance framework, implemented
                automated sync patterns, and achieved ATO approval for
                cross-instance data synchronization.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1017" data-mp="2q86db9cm8sme">
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1018" data-mp="2arzy5njd278u">
                  Secure On-Premise
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1019" data-mp="21rl1xt4uz7qk">
                  Data Governance
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1020" data-mp="25tgczpclczhj">
                  ATO Compliance
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1021" data-mp="2yi3s136qysmn">
                  Cleared Team
                </span>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-1022" data-mp="2oqzdg4kzh9sc">
              <div className="flex items-center gap-3 mb-4" data-id="element-1023" data-mp="3h9daw32eqtqw">
                <div className="w-10 h-10 bg-nrs-navy rounded-lg flex items-center justify-center" data-id="element-1024" data-mp="15t33gklowkzi">
                  <BarChart3Icon className="h-5 w-5 text-nrs-orange" data-id="element-1025" data-mp="10ujf6djmix3z" />
                </div>
                <div data-id="element-1026" data-mp="1fj6496z1z38e">
                  <div className="font-heading font-bold text-nrs-navy text-base" data-id="element-1027" data-mp="1fjpfk4u76tdg">
                    Defense Academic Institution
                  </div>
                  <div className="text-gray-500 text-xs" data-id="element-1028" data-mp="1am5xetudpwg8">
                    Defense Education | Platform Stewardship
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-1029" data-mp="hx8hgxdn4bqn">
                Academic institution platform stewardship program. Developed
                governance framework tailored for educational environment,
                conducted application rationalization across 80+ systems, and
                established Fractional Platform Owner model.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1030" data-mp="237yseh9fo6sg">
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1031" data-mp="2abyjwdn0xom4">
                  App Rationalization
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1032" data-mp="2f96y6351x8i3">
                  Governance Framework
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1033" data-mp="2p7wevsl7omwr">
                  HRSD
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-1034" data-mp="1wkjt3kmvkj4i">
                  ITSM
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="section-white" data-id="element-1035" data-mp="26cg23qqi1tni">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1036" data-mp="18vhe6kjml4dp">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-1037" data-mp="18t4thzmhgfwy">
            <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1038" data-mp="3n2758wvjfghn">
              How New River Systems Stacks Up
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1039" data-mp="3i2cdys7uoow2"></div>
            <p className="text-gray-600" data-id="element-1040" data-mp="3d5c5kanj3a1s">
              An honest comparison of how New River Systems differs from other
              ServiceNow partners
            </p>
          </motion.div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm" data-id="element-1041" data-mp="33ehkh9m0qghx">
            <table className="w-full text-sm min-w-[700px]" data-id="element-1042" data-mp="2zcmkq9jqkb69">
              <thead data-id="element-1043" data-mp="2zb1q6jxf8s8e">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1044" data-mp="2ue1m2ek4tzjx">
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-700 text-xs uppercase w-32" data-id="element-1045" data-mp="2ufw2pgodf5i7">
                    Feature
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-1046" data-mp="2kgwxy96q7u0h">
                    ServiceNow OEM
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-1047" data-mp="h6s8dhgm5ypp">
                    Large SIs
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-1048" data-mp="38v8oc6v5c6t">
                    Staff Aug
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-bold text-nrs-blue text-xs uppercase bg-nrs-light" data-id="element-1049" data-mp="3plabr5vm0q2z">
                    New River
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1050" data-mp="3gn8cauriy5ht">
                {comparisonData.map((row, i) => <tr key={row.feature} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-id="element-1051" data-mp="3ljp17r6jsnvy" data-mpi={i}>
                    <td className="px-4 py-3 font-heading font-semibold text-gray-700 text-xs" data-id="element-1052" data-mp="36nr8yjzqw3p0">
                      {row.feature}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-1053" data-mp="2s093vki9t722">
                      {row.oem}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-1054" data-mp="2nzyuryz6m0iz">
                      {row.si}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-1055" data-mp="31zzwthr6ne01">
                      {row.staffAug}
                    </td>
                    <td className="px-4 py-3 text-nrs-blue font-semibold text-xs bg-nrs-light/50" data-id="element-1056" data-mp="3azd88fyvacne">
                      {row.nrs}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-1057" data-mp="3kraj1llyr2ll">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1058" data-mp="3fpl9ybiawz05">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1059" data-mp="3un45pxtecch3">
            Ready to See Results Like These?
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-8" data-id="element-1060" data-mp="3pp2dondzt2th"></div>
          <p className="text-xl text-nrs-light mb-8 max-w-2xl mx-auto" data-id="element-1061" data-mp="61d3prb5xg41">
            Schedule a free platform assessment and let's discuss what
            governance can do for your agency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1062" data-mp="f2ta7nyuwy2z">
            <a href="mailto:info@newriversystems.com" className="btn-primary" data-id="element-1063" data-mp="b0pyqzc6tthp">
              Schedule Free Assessment
            </a>
            <a href="#" className="btn-secondary" data-id="element-1064" data-mp="3jfq6y8rww7ei">
              Download Case Study PDF
            </a>
          </div>
        </div>
      </section>
    </div>;
}